import {
	AddAJobInterface,
	RejectCandidateInterface,
	SaveJobInterface,
	SearchJobsInterface,
} from 'constants/interfaces';
import * as Yup from 'yup';

type TranslateFunction = (key: string, params?: Record<string, string>) => string;

export const addAJobInitialValues: AddAJobInterface = {
	title: '',
	countryId: null,
	stateId: null,
	cityId: null,
	industryId: null,
	levelId: null,
	workMode: '',
	newRole: [],
	jobRole: [],
	language: [],
	jobType: '',
	salaryType: '',
	company: '',
	location: '',
	skills: [],
	description: '',
	isJobBoost: false,
	address: '',
	minAmount: 1,
	maxAmount: 1,
};

export const SearchJobsInitialValues: SearchJobsInterface = {
	keyword: '',
	cityId: [],
	stateId: [],
	countryId: [],
	location: [],
	industryId: '',
};
export const SaveJobInitialValues: SaveJobInterface = {
	title: '',
	city: null,
	state: null,
	country: null,
	location: '',
	industryId: null,
};

export const RejectCandidateInitialValues: RejectCandidateInterface = {
	reason: '',
	comment: '',
};

export const addAJobSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		title: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.jobTitle') }))
			.trim(),
		countryId: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.country') }),
		),
		stateId: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.state') }),
		),
		cityId: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.city') }),
		),
		company: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.companyType') }),
		),
		industryId: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.companyType') }),
		),
		workMode: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.workMode') }),
		),
		jobRole: Yup.array()
			.min(
				1,
				translate('Validation.minArr', { field: translate('Validation.role'), number: '${min}' }),
			)
			.max(5, translate('Validation.maxArr', { field: translate('Validation.role'), number: '6' }))
			.required(translate('Validation.required', { field: translate('Validation.role') })),
		language: Yup.array()
			.min(1, translate('Validation.required', { field: translate('Validation.language') }))
			.max(
				10,
				translate('Validation.maxArr', { field: translate('Validation.language'), number: '11' }),
			)
			.required(translate('Validation.required', { field: translate('Validation.language') })),
		levelId: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.experience') }),
		),
		jobType: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.employmentType') }),
		),
		salaryType: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.salaryType') }),
		),
		minAmount: Yup.number()
			.required(translate('Validation.required', { field: translate('Validation.minimumSalary') }))
			.min(
				1,
				translate('Validation.minSalary', {
					field: translate('Validation.minimumSalary'),
					number: '0',
				}),
			),
		maxAmount: Yup.number()
			.required(translate('Validation.required', { field: translate('Validation.maximumSalary') }))
			.min(
				Yup.ref('minAmount'),
				translate('Validation.maxSalary', {
					field: translate('Validation.maximumSalary'),
					number: '${min}',
				}),
			),
		location: Yup.string().required(
			translate('Validation.required', { field: translate('Job.jobLocation') }),
		),
		skills: Yup.array()
			.min(
				1,
				translate('Validation.minArr', { field: translate('Validation.skill'), number: '${min}' }),
			)
			.max(
				10,
				translate('Validation.maxArr', { field: translate('Validation.skill'), number: '11' }),
			)
			.required(translate('Validation.required', { field: translate('Validation.skill') })),
		description: Yup.string()
			.required(translate('Validation.required', { field: translate('Validation.description') }))
			.trim(),
		isJobBoost: Yup.boolean().required(
			translate('Validation.required', { field: translate('Validation.jobBoost') }),
		),
	});

export const searchJobsSchema = (translate: TranslateFunction) =>
	Yup.object()
		.shape({
			keyword: Yup.string().trim(),
			location: Yup.array(),
			industryId: Yup.string(),
		})
		.test('at-least-one', function (value) {
			const { keyword, location, industryId } = value;
			if (!!keyword || (Array.isArray(location) && location.length > 0) || !!industryId) {
				return true;
			} else {
				return this.createError({
					path: 'keyword',
					message: translate('Validation.atLeastOne'),
				});
			}
		});

export const saveJobSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		title: Yup.string()
			.required(translate('Validation.required', { field: translate('Validation.jobTitle') }))
			.trim(),
		country: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.country') }),
		),
		state: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.state') }),
		),
		city: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.city') }),
		),
		location: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.jobLocation') }),
		),
		industryId: Yup.number().required(
			translate('Validation.required', { field: translate('Validation.companyIndustry') }),
		),
	});

export const rejectCandidateSchema = (translate: TranslateFunction) =>
	Yup.object().shape({
		reason: Yup.string()
			.required(translate('Validation.required', { field: translate('Validation.reason') }))
			.trim(),
		comment: Yup.string()
			.required(translate('Validation.required', { field: translate('Validation.comment') }))
			.trim(),
	});
